// HomePage.js
import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import { LucideServer, LucideShield, LucideCloud, LucideZap, LucideDatabase, LucideBarChart } from "lucide-react";
import Navbar from "../components/ui/Navbar";

const services = [
  {
    icon: <LucideZap className="h-10 w-10 text-primary" />, 
    title: "Workflow Automation with Power Automate",
    description:
      "Optimize your business processes by automating repetitive tasks with Power Automate. This tool enables seamless integration across various applications, reducing manual effort and increasing operational efficiency."
  },
  {
    icon: <LucideBarChart className="h-10 w-10 text-primary" />, 
    title: "Data Analytics with Power BI",
    description:
      "Transform raw data into meaningful insights using Power BI. Create interactive dashboards and reports that facilitate informed decision-making and uncover trends vital to your business strategy."
  },
  {
    icon: <LucideDatabase className="h-10 w-10 text-primary" />, 
    title: "Database Management with Oracle",
    description:
      "Secure and manage your data effectively with Oracle Database solutions. Benefit from robust performance, scalability, and reliability to handle complex data environments tailored to your organizational needs."
  },
  {
    icon: <LucideCloud className="h-10 w-10 text-primary" />, 
    title: "Cloud Solutions with Microsoft Azure",
    description:
      "Leverage Microsoft Azure's cloud platform to build, deploy, and manage applications through a global network of data centers. Enhance flexibility, scalability, and security in your IT infrastructure."
  },
  {
    icon: <LucideServer className="h-10 w-10 text-primary" />, 
    title: "Collaboration Tools with Microsoft 365",
    description:
      "Enhance workplace productivity with Microsoft 365's suite of tools, including email hosting, advanced threat protection, and compliance solutions, ensuring secure and efficient collaboration."
  },
  {
    icon: <LucideZap className="h-10 w-10 text-primary" />, 
    title: "Data Integration with ETL Services",
    description:
      "Streamline your data integration process with ETL (Extract, Transform, Load) services, facilitating efficient data consolidation, transformation, and storage for effective data-driven decision-making."
  },
  {
    icon: <LucideShield className="h-10 w-10 text-primary" />, 
    title: "Comprehensive Cybersecurity Solutions",
    description:
      "Fortify your digital assets with cutting-edge security measures, risk management strategies, and continuous monitoring to safeguard your company's critical information against evolving threats."
  },
  {
    icon: <LucideServer className="h-10 w-10 text-primary" />, 
    title: "AI & Automation Services",
    description:
      "Explore our comprehensive suite of AI-driven solutions, including Python programming, robotic process automation with UI Path, automated testing with Selenium, scalable Java applications, and cutting-edge Artificial Intelligence technologies."
  },
  {
    icon: <LucideCloud className="h-10 w-10 text-primary" />, 
    title: "AI Agent Fine-Tuning",
    description:
      "Enhance your AI agents' capabilities through meticulous fine-tuning, enabling them to tackle complex tasks, improve decision-making, and deliver highly contextual responses tailored to your business needs."
  }
];

const HomePage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      {/* Hero Section */}
      <section className="relative text-center py-28 bg-gradient-to-r from-blue-600 to-teal-600 text-white">
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <h1 className="text-5xl font-bold mb-6">Empower Your Business with Query Minds</h1>
          <p className="text-lg mb-8">
            Delivering cutting-edge IT services and business automation solutions to drive innovation and efficiency.
          </p>
          <Link to="/about">
            <Button variant="outline" className="bg-white text-blue-600 hover:bg-blue-600 hover:text-white">
              Learn More About Us
            </Button>
          </Link>
        </motion.div>
      </section>

      {/* Modern Feature Section */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50 text-center">
        <div className="container mx-auto">
          <h2 className="text-4xl font-bold mb-6">Why Choose Query Minds?</h2>
          <p className="text-lg text-gray-600 mb-10">
            At Query Minds, we combine innovation, expertise, and technology to deliver results that matter.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-6 bg-white shadow-lg rounded-lg">
              <LucideCloud className="h-12 w-12 text-teal-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Scalable Cloud Solutions</h3>
              <p className="text-gray-600">
                Harness the power of the cloud to scale your operations effortlessly with our customized cloud services.
              </p>
            </div>
            <div className="p-6 bg-white shadow-lg rounded-lg">
              <LucideShield className="h-12 w-12 text-teal-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Unmatched Security</h3>
              <p className="text-gray-600">
                Protect your business with advanced cybersecurity measures tailored to evolving threats.
              </p>
            </div>
            <div className="p-6 bg-white shadow-lg rounded-lg">
              <LucideZap className="h-12 w-12 text-teal-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Automated Workflows</h3>
              <p className="text-gray-600">
                Streamline your business processes with cutting-edge automation solutions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold mb-10">Our Expert Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
              >
                <Card className="shadow-lg hover:shadow-2xl transition-shadow duration-300">
                  <CardHeader className="flex items-center justify-center p-6">
                    {service.icon}
                  </CardHeader>
                  <CardContent className="p-4 text-center">
                    <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-12 bg-gradient-to-r from-teal-500 to-blue-500 text-white text-center">
        <h2 className="text-3xl font-bold mb-6">Let’s Build the Future Together</h2>
        <p className="text-lg mb-8">
          Contact Query Minds today to learn how we can help transform your business with tailored IT solutions.
        </p>
        <Link to="/contact">
          <Button variant="solid" className="bg-blue-600 text-white hover:bg-white hover:text-blue-600 border-2 border-white">
            Get in Touch
          </Button>
        </Link>
      </section>

      {/* Live Chat Widget */}
      <section className="fixed bottom-4 right-4">
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            zIndex: 1000,
          }}
        >
          <button
            style={{
              backgroundColor: "#38bdf8",
              color: "white",
              borderRadius: "50%",
              padding: "15px",
              border: "none",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
              cursor: "pointer",
            }}
            onClick={() => alert("Live Chat Coming Soon!")}
          >
            💬
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-gray-800 text-white text-center">
        <p className="mb-2">&copy; {new Date().getFullYear()} Query Minds. All rights reserved.</p>
        <div className="flex justify-center space-x-4">
        <button onClick={() => alert('Privacy Policy coming soon!')} className="hover:underline">Privacy Policy</button>
        <button onClick={() => alert('Terms of Service coming soon!')} className="hover:underline">Terms of Service</button>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
